import { useEffect, useRef, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { getUserAuthInfo, waitForUserAuth, renewToken, forceLogout, isHardAuthFailure } from "./utils/AuthService";
import { getUserInfoCoalesced } from "./api/General/account";
import { connect } from "react-redux";
import { init, setLoading, setBootError } from "./store/redux/actions/userActionCreator";
import LandingPage from "./components/LandingPage";
import { BrandLoader } from "./components/ui/BrandLoader";
import BootErrorPanel from "./components/BootErrorPanel";
import { track, AnalyticsEvents } from "./utils/analytics";

const PrivateRoute = (props: any) => {
    const isInitializing = useRef(false);
    // Bumped by the retry button. It is in the effect deps so a retry actually
    // re-fires the fetch — the old effect depended only on `firstLoad`, and the
    // catch path left `firstLoad` untouched, so a failed boot could NEVER
    // re-run and the session was stuck until the tab was closed.
    const [retryNonce, setRetryNonce] = useState(0);
    // Which retry generation has already been booted. INIT flips `firstLoad`,
    // which re-fires the effect; without this the retry generation would boot a
    // second time and fire another ~31s GetUserInfo for nothing.
    const bootedNonce = useRef<number | null>(null);

    // "Hydrated" = the INIT payload actually landed. userId/role/fullName come
    // ONLY from INIT; SET_USER_AUTH_INFO sets isLoggedIn/loading and nothing else.
    const userHydrated = !!props.userId && !!props.role;

    // One telemetry line when we are signed in but the slice is empty AFTER the
    // boot has finished. Before this, that state produced no request, no error
    // and no event — it was invisible to everything except a code read.
    const reportedInvalid = useRef(false);
    useEffect(() => {
        if (props.isLoggedIn && !props.loading && !props.firstLoad && !userHydrated && !reportedInvalid.current) {
            reportedInvalid.current = true;
            track(AnalyticsEvents.BootStateInvalid, {
                role: props.role ?? '',
                hasUserId: !!props.userId,
                firstLoad: !!props.firstLoad,
                path: typeof window !== 'undefined' ? window.location.pathname : '',
            });
        }
    }, [props.isLoggedIn, props.loading, props.firstLoad, props.role, props.userId, userHydrated]);

    // A LOGOUT resets the slice (firstLoad back to true) without unmounting this
    // component. Re-arm the boot guard on that transition, or the next session
    // would sit on the loader with no fetch ever issued.
    const prevFirstLoad = useRef(props.firstLoad);
    useEffect(() => {
        if (props.firstLoad && !prevFirstLoad.current) {
            bootedNonce.current = null;
        }
        prevFirstLoad.current = props.firstLoad;
    }, [props.firstLoad]);

    useEffect(() => {
        const fetchUser = async () => {
            if (isInitializing.current) return;

            // A retry has to be able to boot even after `firstLoad` has been
            // flipped to false by a failed INIT, so it is its own trigger.
            const shouldBoot =
                (props.firstLoad || retryNonce > 0) && bootedNonce.current !== retryNonce;

            try {
                if (shouldBoot) {
                    bootedNonce.current = retryNonce;
                    isInitializing.current = true;

                    if (!props.userAuthInfo) {
                        let userAuthInfo = await getUserAuthInfo();
                        if (!userAuthInfo) {
                            userAuthInfo = await waitForUserAuth(1500, 50);
                        }

                        if (userAuthInfo) {
                            if (userAuthInfo.expired) {
                                try {
                                    // renewToken fires addUserLoaded on
                                    // success, which authLifecycle turns into
                                    // a SET_USER_AUTH_INFO dispatch.
                                    const refreshed = await renewToken();
                                    if (!refreshed) {
                                        await forceLogout('boot-renew-returned-null');
                                        return;
                                    }
                                    userAuthInfo = refreshed;
                                } catch (e) {
                                    // Only tear down the session on a hard
                                    // "your IdP session is gone" error. A
                                    // transient network/timeout at boot
                                    // shouldn't kick the user out — the 401
                                    // interceptor will retry renewal on the
                                    // next API call once the network recovers.
                                    if (isHardAuthFailure(e)) {
                                        await forceLogout('boot-renew-failed-hard');
                                        return;
                                    }
                                    console.warn('[auth] boot-time renew failed (transient, continuing with stale token):', e);
                                    // fall through with the stale userAuthInfo — getUserInfo
                                    // below may still 401, which the interceptor handles.
                                }
                            }
                            const userInfo = await timedGetUserInfo();
                            props.init(userInfo.payload as any);
                        } else {
                            props.setLoading(false);
                        }
                    } else {
                        const userInfo = await timedGetUserInfo();
                        props.init(userInfo.payload as any);
                    }
                }
            } catch (error) {
                // This used to only console.error + setLoading(false), which
                // rendered the full shell on top of an EMPTY user slice: no
                // role, no userId, reduced nav, and a subscription hook that
                // never fires because it gates on userId. Surface it instead.
                console.error("Error fetching user:", error);
                props.setBootError(true);
                props.setLoading(false);
            } finally {
                isInitializing.current = false;
            }
        };
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.firstLoad, retryNonce]);

    const handleRetry = useCallback(() => {
        props.setBootError(false);
        props.setLoading(true);
        setRetryNonce((n) => n + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (props.loading) {
        // Full-page and on the critical path of every session start, so it uses
        // the same identity as the pre-React splash in index.html.
        return <BrandLoader />;
    }

    if (!props.isLoggedIn) {
        return <LandingPage />;
    }

    if (props.bootError) {
        return <BootErrorPanel onRetry={handleRetry} />;
    }

    // NEVER render the app shell on an unhydrated user slice.
    //
    // `SET_USER_AUTH_INFO` flips `isLoggedIn`/`loading` on its own, but userId,
    // role and fullName arrive ONLY with `INIT`. Gating on loading/isLoggedIn
    // alone therefore mounted MasterLayout while GetUserInfo was still in
    // flight, and every downstream consumer degraded silently: the nav built
    // `effectiveRoles = new Set([""])`, the avatar fell back to 'U', and
    // useMySubscription returned early on a null userId so the layout sat on a
    // skeleton with no request ever sent. Wait for the payload instead.
    //
    // Deliberately keyed on the DATA, not on `firstLoad`: a returning session
    // rehydrates userId/role from redux-persist and must keep rendering
    // immediately while the boot refresh runs behind it.
    if (!userHydrated) {
        // The boot has already finished (firstLoad was cleared by INIT) and the
        // slice is STILL empty — i.e. the payload came back without a role/id.
        // Show the actionable panel rather than a loader that would never end.
        if (!props.firstLoad) {
            return <BootErrorPanel onRetry={handleRetry} />;
        }
        return <BrandLoader />;
    }

    return <Outlet />;
};

/**
 * The boot GetUserInfo call, wrapped so every attempt reports how long it took
 * and whether it resolved. Cold this endpoint has been measured at ~31s, and a
 * code read could not tell whether the observed cold-load failures were a slow
 * success, a rejection or a true hang. This is the measurement that settles it.
 *
 * `ms` is measured from the REAL request start (supplied by the coalescer), not
 * from the moment we joined it — otherwise a de-duplicated call would be logged
 * as a fast success and the endpoint would look like it had got faster when it
 * had only stopped being called twice. `deduped` carries that distinction into
 * the event so the first deploy's telemetry can be read honestly.
 *
 * Sharing the promise does NOT share error handling: a rejection is delivered
 * to every awaiting caller, so this catch still runs — and still rethrows into
 * the boot catch that raises the retry panel — whether we started the request
 * or joined MasterLayout's.
 */
async function timedGetUserInfo() {
    const { promise, deduped, startedAt } = getUserInfoCoalesced();
    const elapsed = () => Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt);
    try {
        const result = await promise;
        track(AnalyticsEvents.BootUserInfo, { ms: elapsed(), ok: true, deduped });
        return result;
    } catch (e) {
        track(AnalyticsEvents.BootUserInfo, { ms: elapsed(), ok: false, deduped });
        throw e;
    }
}


const mapStateToProps = (state: any) => {
    return {
        loading: state.user.loading,
        firstLoad: state.user.firstLoad,
        isLoggedIn: state.user.isLoggedIn,
        userAuthInfo: state.user.userAuthInfo,
        userId: state.user.userId,
        role: state.user.role,
        bootError: state.user.bootError,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        init: (input: any) => dispatch(init(input)),
        setLoading: (input: any) => dispatch(setLoading(input)),
        setBootError: (input: any) => dispatch(setBootError(input)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute);
